import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/images/LOGO.png';

const Navbar = () => {
    const [activeSection, setActiveSection] = useState('home');
    const location = useLocation();

    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        // Only run scroll detection on the home page
        if (location.pathname !== '/') {
            setActiveSection('');
            return;
        }

        const sections = ['home', 'services', 'profile', 'about', 'clients', 'career', 'contact'];

        const observerOptions = {
            root: null,
            rootMargin: '-50% 0px -50% 0px', // Trigger when section is in the middle of the viewport
            threshold: 0
        };

        const observerCallback = (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setActiveSection(entry.target.id);
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);

        sections.forEach((id) => {
            const element = document.getElementById(id);
            if (element) observer.observe(element);
        });

        return () => observer.disconnect();
    }, [location.pathname]);

    // Close mobile menu on route or hash change
    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname, location.hash]);

    const navLinks = [
        { name: 'Home', href: '/', id: 'home', isRoute: true },
        { name: 'Services', href: '/#services', id: 'services' },
        { name: 'Company Profile', href: '/#profile', id: 'profile' },
        { name: 'About Us', href: '/#about', id: 'about' },
        { name: 'Clients', href: '/#clients', id: 'clients' },
        // { name: 'Careers', href: '/#career', id: 'career' },
        { name: 'Contact Us', href: '/#contact', id: 'contact' },
    ];

    return (
        <nav className="bg-[#0a1d37] text-white sticky top-0 z-50">
            <div className="container mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
                <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                    <img src={logo} alt="DBQ Logo" className="h-10 w-10 object-contain" />
                    <span className="text-lg font-medium tracking-tight">DBQ Technologies</span>
                </Link>

                <div className="hidden md:flex items-center gap-8">
                    {navLinks.map((link) => (
                        link.isRoute ? (
                            <Link
                                key={link.id}
                                to={link.href}
                                className={`text-[13px] font-semibold transition-colors uppercase tracking-wider ${activeSection === link.id ? 'text-orange-400 font-bold' : 'hover:text-orange-400'
                                    }`}
                            >
                                {link.name}
                            </Link>
                        ) : (
                            <a
                                key={link.id}
                                href={link.href}
                                className={`text-[13px] font-semibold transition-colors uppercase tracking-wider ${activeSection === link.id ? 'text-orange-400 font-bold' : 'hover:text-orange-400'
                                    }`}
                            >
                                {link.name}
                            </a>
                        )
                    ))}
                </div>

                <button
                    className="md:hidden p-2 focus:outline-none"
                    onClick={() => setIsOpen(!isOpen)}
                    aria-label="Toggle Menu"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {isOpen ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                        )}
                    </svg>
                </button>
            </div>

            {/* Mobile Menu */}
            <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
                <div className="bg-[#0d2446] px-6 py-4 flex flex-col gap-4 border-t border-white/10">
                    {navLinks.map((link) => (
                        link.isRoute ? (
                            <Link
                                key={link.id}
                                to={link.href}
                                className={`text-[13px] font-semibold transition-colors uppercase tracking-wider py-2 ${activeSection === link.id ? 'text-orange-400' : 'hover:text-orange-400'
                                    }`}
                            >
                                {link.name}
                            </Link>
                        ) : (
                            <a
                                key={link.id}
                                href={link.href}
                                className={`text-[13px] font-semibold transition-colors uppercase tracking-wider py-2 ${activeSection === link.id ? 'text-orange-400' : 'hover:text-orange-400'
                                    }`}
                            >
                                {link.name}
                            </a>
                        )
                    ))}
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
